import { useState } from 'react'
import { Control, useController } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { FormInput } from '@/components/newRedesign/form/FormInput'
import { FormTextarea } from '@/components/newRedesign/form/FormTextarea'
import { FormSearchableSelect } from '@/components/newRedesign/form/FormSearchableSelect'
import { SelectWithAddButton } from '@/components/newRedesign/components/SelectWithAddButton'
import { CreateOriginDestinyDialog } from '@/components/newRedesign/components/CreateOriginDestinyDialog'

import {
  createOriginDestiny,
  type CreateOriginDestinyParams,
} from '@/api/originDestinyAPI'

import { CreateContainerFormData } from '../CreateContainerWizard'
import {
  useCountriesFetcher,
  useCategoriesFetcher,
  useOriginsFetcher,
  useDestinationsFetcher,
} from '../../hooks/useContainerQueries'

interface DetailsStepProps {
  control: Control<CreateContainerFormData>
}

export function DetailsStep({ control }: DetailsStepProps) {
  const queryClient = useQueryClient()
  const [showOriginDialog, setShowOriginDialog] = useState(false)
  const [showDestinyDialog, setShowDestinyDialog] = useState(false)

  // Controllers for origin and destiny
  const { field: originField } = useController({
    name: 'originId',
    control,
  })

  const { field: destinyField } = useController({
    name: 'destinyId',
    control,
  })

  // Usar fetchers con cache de React Query
  const fetchCountries = useCountriesFetcher()
  const fetchCategories = useCategoriesFetcher()
  const fetchOrigins = useOriginsFetcher()
  const fetchDestinations = useDestinationsFetcher()

  // Mutation for creating origin/destiny
  const createOriginDestinyMutation = useMutation({
    mutationFn: (data: CreateOriginDestinyParams) => createOriginDestiny(data),
    onSuccess: () => {
      toast.success('Origen/Destino creado exitosamente')
      queryClient.invalidateQueries({ queryKey: ['origin_destiny'] })
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error?.response?.data?.message || 'Error al crear origen/destino'
      )
    },
  })

  const handleSaveOrigin = (data: { name: string; countryId?: number }) => {
    if (!data.countryId) {
      toast.error('El país es requerido')
      return
    }
    createOriginDestinyMutation.mutate(
      { name: data.name, countryId: data.countryId },
      {
        onSuccess: () => {
          setShowOriginDialog(false)
        },
      }
    )
  }

  const handleSaveDestiny = (data: { name: string; countryId?: number }) => {
    if (!data.countryId) {
      toast.error('El país es requerido')
      return
    }
    createOriginDestinyMutation.mutate(
      { name: data.name, countryId: data.countryId },
      {
        onSuccess: () => {
          setShowDestinyDialog(false)
        },
      }
    )
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        {/* Name - Full Width Row 1 */}
        <FormInput
          control={control}
          inputSize="md"
          label="Nombre"
          name="name"
          placeholder="Ingrese el nombre del contenedor"
          rules={{ required: 'El nombre es requerido' }}
          showLabel
        />

        {/* Country - Full Width Row 2 */}
        <FormSearchableSelect
          control={control}
          emptyMessage="No se encontraron países"
          fetcher={fetchCountries}
          label="País de origen"
          name="countryFromId"
          placeholder="Seleccionar país"
          searchPlaceholder="Buscar país..."
          showLabel
        />

        {/* Origin and Destiny - Row 3 - Side by Side on larger screens */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Origin */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
              Origen
            </label>
            <SelectWithAddButton
              emptyMessage="No se encontraron orígenes"
              fetcher={fetchOrigins}
              onAdd={() => setShowOriginDialog(true)}
              onChange={originField.onChange}
              placeholder="Seleccionar origen"
              searchPlaceholder="Buscar origen..."
              value={originField.value}
            />
          </div>

          {/* Destiny */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
              Destino
            </label>
            <SelectWithAddButton
              emptyMessage="No se encontraron destinos"
              fetcher={fetchDestinations}
              onAdd={() => setShowDestinyDialog(true)}
              onChange={destinyField.onChange}
              placeholder="Seleccionar destino"
              searchPlaceholder="Buscar destino..."
              value={destinyField.value}
            />
          </div>
        </div>

        {/* Categories - Full Width Row 4 */}
        <FormSearchableSelect
          control={control}
          emptyMessage="No se encontraron categorías"
          fetcher={fetchCategories}
          label="Categorías"
          multiple
          name="categories"
          placeholder="Seleccionar categorías"
          searchPlaceholder="Buscar categorías..."
          showLabel
        />

        {/* Code, Tracking Number - Row 5 on larger screens */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <FormInput
            control={control}
            inputSize="md"
            label="Código"
            name="code"
            placeholder="Código"
            showLabel
          />

          <FormInput
            control={control}
            inputSize="md"
            label="Número de tracking"
            name="trackingNumber"
            placeholder="Número de tracking"
            showLabel
          />
        </div>

        {/* Observations - Full Width Row 6 */}
        <FormTextarea
          control={control}
          label="Observaciones"
          name="observations"
          placeholder="Ingrese observaciones..."
          rows={3}
          showLabel
        />
      </div>

      {/* Origin Dialog */}
      <CreateOriginDestinyDialog
        inputLabel="Nombre del origen"
        inputPlaceholder="Ingrese el nombre del origen"
        isPending={createOriginDestinyMutation.isPending}
        onCancel={() => setShowOriginDialog(false)}
        onOpenChange={setShowOriginDialog}
        onSave={handleSaveOrigin}
        open={showOriginDialog}
        title="Crear Origen"
      />

      {/* Destiny Dialog */}
      <CreateOriginDestinyDialog
        inputLabel="Nombre del destino"
        inputPlaceholder="Ingrese el nombre del destino"
        isPending={createOriginDestinyMutation.isPending}
        onCancel={() => setShowDestinyDialog(false)}
        onOpenChange={setShowDestinyDialog}
        onSave={handleSaveDestiny}
        open={showDestinyDialog}
        title="Crear Destino"
      />
    </>
  )
}
